// Chat con polling REST (5s) en fase 4A. En 4B se sustituye por socket.io manteniendo la misma UI.

import { useCallback, useEffect, useRef, useState } from 'react';
import { listMessages, sendMessage, type ChatMessage } from '@/services/classrooms';
import styles from './ChatPanel.module.scss';

interface ChatPanelProps {
  classroomId: number | null;
  currentUserId: number;
  enabled: boolean;
}

export function ChatPanel({ classroomId, currentUserId, enabled }: ChatPanelProps) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const endRef = useRef<HTMLDivElement>(null);
  const lastIdRef = useRef<number | undefined>(undefined);

  const poll = useCallback(async () => {
    if (!classroomId || !enabled) return;
    try {
      const next = await listMessages(classroomId, lastIdRef.current);
      if (next.length === 0) return;
      setMessages((prev) => {
        const merged = [...prev, ...next];
        return merged;
      });
      lastIdRef.current = next[next.length - 1]!.id;
    } catch {
      // silencioso; reintentaremos en el siguiente tick
    }
  }, [classroomId, enabled]);

  useEffect(() => {
    if (!classroomId || !enabled) return;
    setMessages([]);
    lastIdRef.current = undefined;
    void poll();
    const iv = setInterval(() => { void poll(); }, 5000);
    return () => clearInterval(iv);
  }, [classroomId, enabled, poll]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages]);

  async function onSend() {
    if (!classroomId || !input.trim() || sending) return;
    setSending(true);
    try {
      await sendMessage(classroomId, input.trim());
      setInput('');
      await poll();
    } catch {
      // mantenemos el texto; el usuario puede reintentar
    } finally {
      setSending(false);
    }
  }

  return (
    <div className={styles.panel}>
      <header className={styles.header}>
        <h3>Chat</h3>
      </header>

      <div className={styles.messages}>
        {!enabled ? (
          <p className={styles.disabled}>El chat estará disponible cuando se abra el aula.</p>
        ) : messages.length === 0 ? (
          <p className={styles.empty}>Sé el primero en escribir.</p>
        ) : (
          messages.map((m) => {
            const own = m.user_id === currentUserId;
            return (
              <div key={m.id} className={`${styles.message} ${own ? styles.messageOwn : ''}`}>
                <div className={styles.messageHeader}>
                  <strong>{own ? 'Tú' : m.user_display_name}</strong>
                  <span>{new Date(m.created_at).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}</span>
                </div>
                <div className={styles.messageContent}>{m.content}</div>
              </div>
            );
          })
        )}
        <div ref={endRef} />
      </div>

      <div className={styles.composer}>
        <input
          type="text"
          placeholder={enabled ? 'Escribe un mensaje…' : 'Aula cerrada'}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void onSend(); } }}
          disabled={!enabled || sending}
          maxLength={2000}
        />
        <button onClick={() => void onSend()} disabled={!enabled || sending || !input.trim()}>
          Enviar
        </button>
      </div>
    </div>
  );
}
